import ChevronDownIcon from "@shared/svg/chevron-down.svg?react";
import ChevronRightIcon from "@shared/svg/chevron-right.svg?react";
import { Check, MagnifyingGlass, Tag } from "phosphor-react";
import { Avatar } from "@shared/ui/Avatar";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "@shared/ui/Button";
import { Checkbox, CHECKBOX_SIZES } from "@shared/ui/Inputs";
import { findIndex } from "@shared/utils/arrayUtils";
import { trimText } from "@shared/utils/textFormatting";
import React, { useEffect, useRef, useState } from "react";
import useOnClickOutside from "use-onclickoutside";
import {
  MULTISELECT_VARIANTS,
  MultiSelectVariantType,
} from "./AsyncMultiSelectDropdown";

import DropdownItem from "../DropdownItem";
import { getOptionBackgroundColor } from "@shared/utils/colors";

type MultiSelectDropdownPropsType = {
  className?: string;
  isWholeSelectClickable?: boolean;
  labelTextOverride?: string;
  defaultValue?: any[];
  options?: any[];
  allowMulti?: boolean;
  textAllSelect?: string;
  trimCharCount?: number;
  placeholder?: string;
  sortingOption?: React.ReactElement;
  dropdownClassName?: string;
  dropdownTitle?: string;
  showSearchInput?: boolean;
  searchPlaceholder?: string;
  onCloseRequest?: (data: any[]) => void;
  onOpen?: (val: boolean) => void;
  showAvatar?: boolean;
  onChange?: (data: any[]) => void;
  maxOptionLabelWidth?: string;
  useDropdownContainer?: boolean;
  variant?: MultiSelectVariantType;
  showActiveIndicator?: boolean;
};

const MultiSelectDropdown = ({
  className = "",
  isWholeSelectClickable = true,
  labelTextOverride = "",
  defaultValue = [],
  options = [],
  allowMulti = false,
  textAllSelect = "",
  trimCharCount = 20,
  placeholder = "",
  sortingOption = null,
  dropdownClassName = "",
  dropdownTitle = "",
  showSearchInput = false,
  searchPlaceholder = "",
  onCloseRequest = () => {},
  onOpen = () => {},
  showAvatar = false,
  onChange = () => {},
  maxOptionLabelWidth = "",
  useDropdownContainer = false,
  variant = MULTISELECT_VARIANTS.DEFAULT,
  showActiveIndicator = false,
}: MultiSelectDropdownPropsType) => {
  const mounted = useRef();
  const containerRef = useRef();
  const [selectedItems, setSelectedItems] = useState<any[]>(defaultValue);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [hoveredItemValue, setHoveredItemValue] = useState<string | null>(null);

  useEffect(() => {
    setSelectedItems(defaultValue);
  }, [defaultValue]);

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!mounted.current) return;
    setSearchText(e.target.value);
  };

  const handleCloseRequest = () => {
    onCloseRequest(selectedItems);
  };

  const handleOptionClick = (option: any) => {
    if (!mounted.current) return;

    if (!allowMulti) {
      onChange([option]);
      setSelectedItems([option]);
      setShowDropdown(false);
      onOpen(false);
      return;
    }
    const index = findIndex(selectedItems, { value: option.value });
    if (index < 0) {
      setSelectedItems((prev) => {
        const updatedList = [...prev, option];
        onChange(updatedList);
        return updatedList;
      });
    } else {
      const clone = [...selectedItems];
      clone.splice(index, 1);
      onChange(clone);
      setSelectedItems(clone);
    }
  };

  useOnClickOutside(containerRef, () => {
    if (mounted.current) {
      if (showDropdown) {
        handleCloseRequest();
        setShowDropdown(false);
        onOpen(false);
        setSearchText("");
      }
    }
  });

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const handleOnClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    if (!mounted.current) return;
    setShowDropdown((prev) => {
      if (prev) {
        handleCloseRequest();
      }
      onOpen(!prev);
      return !prev;
    });
    setSearchText("");
  };

  return (
    <div
      className={`relative flex items-center justify-between w-full p-md font-manrope border border-solid border-primary ${className}`}
      ref={containerRef}
      {...(isWholeSelectClickable ? { onClick: handleOnClick } : {})}
    >
      <BodyText
        size={BODY_TEXT_SIZES.X_SMALL}
        className="font-bold text-primary cursor-pointer block"
      >
        {labelTextOverride
          ? labelTextOverride
          : selectedItems.length === options.length && allowMulti
          ? textAllSelect
          : trimText(
              selectedItems.map((item) => item.label).join(", "),
              trimCharCount,
            ) || placeholder}
      </BodyText>
      <div className="flex items-center gap-x-2">
        {sortingOption}
        <div className="relative shrink-0">
          <ChevronDownIcon
            width={16}
            height={16}
            className={`cursor-pointer text-secondary ${
              showDropdown ? "rotate-180" : ""
            }`}
            {...(isWholeSelectClickable ? {} : { onClick: handleOnClick })}
          />
          {showActiveIndicator && (
            <span
              className="absolute -top-3xs -right-3xs w-sm h-sm bg-danger rounded-full border border-solid border-primary pointer-events-none"
              aria-hidden
            />
          )}
        </div>
      </div>

      <div
        className={`absolute top-full left-0 z-[1] flex-col min-w-full py-sm px-0 overflow-visible bg-primary rounded-md shadow-dropdown mt-xs ${
          showDropdown ? "flex" : "hidden"
        } ${
          variant === MULTISELECT_VARIANTS.DUAL_PANE ? "flex-row" : ""
        } ${dropdownClassName}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {variant !== MULTISELECT_VARIANTS.DUAL_PANE && (
          <>
            {dropdownTitle && (
              <BodyText
                size={BODY_TEXT_SIZES.X_SMALL}
                color="text-secondary"
                className="px-sm pt-sm z-[1]"
              >
                {dropdownTitle}
              </BodyText>
            )}

            {showSearchInput && (
              <div className="px-md py-sm">
                <div className="flex items-center gap-xs px-sm py-2xs bg-[#F1F5F9] rounded-lg border border-solid border-transparent focus-within:border-brand transition-all duration-200">
                  <MagnifyingGlass
                    size={16}
                    className="text-[#94A3B8] shrink-0"
                  />
                  <input
                    className="bg-transparent border-0 outline-none text-sm font-manrope text-primary w-full p-0"
                    placeholder={searchPlaceholder || "Search"}
                    value={searchText}
                    onChange={onSearch}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {variant === MULTISELECT_VARIANTS.DUAL_PANE ? (
          <div className="flex w-full h-72">
            <div
              className="flex-1 overflow-y-auto border-r border-solid border-secondary"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {options
                .filter(
                  (option) =>
                    option.label
                      ?.toLowerCase()
                      .includes(searchText.toLowerCase()),
                )
                .map((option) => (
                  <div
                    key={option.value}
                    onMouseEnter={() => setHoveredItemValue(option.value)}
                    onMouseLeave={() => setHoveredItemValue(null)}
                    className={`flex justify-between items-center py-sm px-md font-manrope text-xs font-medium transition-all duration-200 ease-in-out cursor-pointer ${
                      hoveredItemValue === option.value ? "bg-accent" : ""
                    }`}
                  >
                    <div className="flex items-center min-w-0 flex-1">
                      {showAvatar && (
                        <Avatar className="mr-sm" name={option.label} />
                      )}
                      <BodyText
                        size={BODY_TEXT_SIZES.X_SMALL}
                        className={`${maxOptionLabelWidth} truncate`}
                      >
                        {option.label}
                      </BodyText>
                    </div>
                    {option.children && option.children.length > 0 && (
                      <ChevronRightIcon
                        width={16}
                        height={16}
                        className="text-secondary shrink-0 ml-sm"
                      />
                    )}
                  </div>
                ))}
            </div>

            {hoveredItemValue &&
              options.find((opt) => opt.value === hoveredItemValue)
                ?.children && (
                <div
                  className="flex-1 overflow-y-auto"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <BodyText
                    size={BODY_TEXT_SIZES.X_SMALL}
                    color="text-secondary"
                    className="px-md py-sm z-[1] border-b border-solid border-secondary"
                  >
                    {
                      options.find((opt) => opt.value === hoveredItemValue)
                        ?.label
                    }
                  </BodyText>
                  {options
                    .find((opt) => opt.value === hoveredItemValue)
                    ?.children.filter(
                      (child: any) =>
                        child.label
                          ?.toLowerCase()
                          .includes(searchText.toLowerCase()),
                    )
                    .map((child: any) => (
                      <div
                        key={child.value}
                        onClick={() => handleOptionClick(child)}
                        className="flex justify-between items-center py-sm px-md font-manrope text-xs font-medium text-primary transition-all duration-200 ease-in-out hover:bg-accent cursor-pointer"
                      >
                        <div className="flex items-center min-w-0 flex-1">
                          {showAvatar && (
                            <Avatar className="mr-sm" name={child.label} />
                          )}
                          <BodyText
                            size={BODY_TEXT_SIZES.X_SMALL}
                            className={`${maxOptionLabelWidth} truncate`}
                          >
                            {child.label}
                          </BodyText>
                        </div>
                        {findIndex(selectedItems, { value: child.value }) >=
                          0 && (
                          <Check
                            width={16}
                            height={16}
                            className="text-brand shrink-0 ml-sm"
                          />
                        )}
                      </div>
                    ))}
                </div>
              )}
          </div>
        ) : (
          <div
            className="overflow-y-auto overflow-x-visible"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {allowMulti && textAllSelect && (
              <div
                className="w-full relative flex items-center justify-between py-sm px-md font-manrope text-xs font-medium text-primary transition-all duration-200 ease-in-out hover:bg-accent cursor-pointer"
                onClick={() => {
                  const allSelected = selectedItems.length === options.length;
                  const updatedList = allSelected ? [] : options;
                  setSelectedItems(updatedList);
                  onChange(updatedList);
                }}
              >
                <div className="flex items-center min-w-0">
                  {showAvatar && (
                    <Avatar className="mr-sm" name={textAllSelect} />
                  )}
                  <BodyText
                    size={BODY_TEXT_SIZES.X_SMALL}
                    color="text-primary"
                    className={`truncate ${maxOptionLabelWidth}`}
                  >
                    {textAllSelect}
                  </BodyText>
                </div>
                <div className="shrink-0 ml-sm">
                  <Checkbox
                    size={CHECKBOX_SIZES.SMALL}
                    checked={
                      options.length > 0 &&
                      selectedItems.length === options.length
                    }
                  />
                </div>
              </div>
            )}
            {options
              .filter(
                (option) =>
                  option.label
                    ?.toLowerCase()
                    .includes(searchText.toLowerCase()),
              )
              .map((option) => (
                <DropdownItem
                  key={option.value}
                  option={option}
                  onClick={() => handleOptionClick(option)}
                  found={findIndex(selectedItems, { value: option.value }) >= 0}
                  showAvatar={showAvatar}
                  maxLabelWidth={maxOptionLabelWidth}
                  multiSelect={true}
                  onChildSelect={(child) => {
                    handleOptionClick(child);
                    if (!allowMulti) {
                      setShowDropdown(false);
                      onOpen(false);
                    }
                  }}
                  onResetHover={() => {
                    setHoveredItemValue(null);
                  }}
                  selectedItems={selectedItems}
                />
              ))}
          </div>
        )}

        {allowMulti && selectedItems.length > 0 && (
          <div className="border-t border-solid border-secondary p-sm">
            <Button
              variant={BUTTON_VARIANTS.LINK}
              size={BUTTON_SIZES.SMALL}
              fullWidth
              text="Remove All"
              className="!justify-start px-sm py-xs hover:bg-accent hover:text-brand rounded"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedItems([]);
                onChange([]);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelectDropdown;
